import { useEffect, useMemo, useState } from 'react';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import DialogStandard from '@bumble/app/components/DialogStandard';
import { Loading } from '@bumble/app/components/Loading';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import { Indicator } from '@bumble/app/components/Indicator';
import { UserAgentCreate } from '@bumble/app/components/user-agent/UserAgentCreate';
import { UserAgentLink } from '@bumble/app/components/user-agent/UserAgentLink';
import { UserAgentUnlink } from '@bumble/app/components/user-agent/UserAgentUnlink';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { Card, CardContent } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import { UserAgent } from '../__generated__/data-contracts';

const columns: GridColDef[] = [
  {
    field: 'name',
    headerName: 'User Agent',
    flex: 1,
    minWidth: 200,
    disableColumnMenu: true,
  },
  {
    field: 'user_agent_id',
    headerName: 'User Agent ID',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'email',
    headerName: 'Email',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'app_service_id',
    headerName: 'App Service ID',
    width: 200,
    disableColumnMenu: true,
  },
  /* -----------------------------------------
  {
    field: 'description',
    headerName: 'Description',
    width: 300,
    disableColumnMenu: true,
  },
  {
    field: 'admin_state',
    headerName: 'Admin State',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'admin_state_change_at',
    headerName: 'Admin State Changed at',
    minWidth: 150,
    disableColumnMenu: true,
    valueGetter: (params) => {
      if (params.value === null) return '';
      return DateTime.fromISO(params.value).toLocaleString(DateTime.DATETIME_SHORT);
    },
  },
  */

  {
    field: 'op_state',
    headerName: 'OP State',
    width: 150,
    disableColumnMenu: true,
    renderCell: (params) => {
      return params.value !== undefined ? <Indicator active={params.value === 'up'} /> : 'N/A';
    }
  },
  {
    field: 'op_state_change_at',
    headerName: 'OP State Changed at',
    minWidth: 150,
    disableColumnMenu: true,
    valueGetter: (value) => {
      if (value === null) return '';
      return DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_SHORT);
    },
  },
  {
    field: 'last_auth_status',
    headerName: 'Last Auth Status',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'last_auth_time',
    headerName: 'Last Auth Time',
    minWidth: 150,
    disableColumnMenu: true,
    valueGetter: (value) => {
      if (value === null) return '';
      return DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_SHORT);
    },
  },
  {
    field: 'created_at',
    headerName: 'Created at',
    minWidth: 150,
    headerAlign: 'right',
    align: 'right',
    disableColumnMenu: true,
    valueGetter: (value) => {
      if (value === null) return '';
      return DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_SHORT);
    },
  },
];

const columnVisible = {
  name: true,
  user_agent_id: true,
  email: true,
  app_service_id: true,
  admin_state: true,
  admin_state_change_at: true,
  op_state: true,
  op_state_change_at: true,
  last_auth_status: true,
  last_auth_time: true,
  created_at: true,
};

export const UserAgents = () => {
  const [userAgentCreate, showUserAgentCreate] = useState(false);
  const [userAgentLink, showUserAgentLink] = useState(false);
  const [userAgentUnlink, showUserAgentUnlink] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection);
  };
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const selectedRowItem = useMemo(() => {
    return rowSelectionModel.length === 1 ? rowSelectionModel[0].toString() : '';
  }, [rowSelectionModel]);
  useEffect(() => {
    if (rowSelectionModel.length === 1) {
      setSelectedItemId(selectedRowItem);
    } else {
      setSelectedItemId('');
    }
  }, [rowSelectionModel, selectedRowItem]);

  const api = useApi();
  const { isLoading, data, refetch } = useQuery(
    ['userAgents'],
    api.listUserAgentApiV1ListUserAgentGet.bind(null, {}),
    { enabled: true },
  );

  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);

  const handleDataRefetch = () => {
    refetch();
    toast.success('Refreshed User Agents.');
  };

  const removeUserAgent = useMutation(api.removeUserAgentApiV1RemoveUserAgentPost, {
    onSuccess() {
      toast.success('User Agent removed.');
      refetch();
    },
    onError: apiError,
  });

  const handleUserAgentDelete = () => {
    removeUserAgent.mutate({
      user_agent_id: selectedItemId,
    });
  };

  const handleCreateUserAgent = () => {
    showUserAgentCreate(true);
  };

  const handleLinkUserAgent = () => {
    showUserAgentLink(true);
  };

  const handleUnlinkUserAgent = () => {
    showUserAgentUnlink(true);
  };

  const handleDelete = () => {
    setShowConfirmDelete(true);
  };

  return (
    <PageContent>
      <PageHeader
        pageName="User Agents"
        count={data?.data?.length || 0}
      // description="On this page, you have the flexibility to create new user agents or remove those that are no longer in use. Additionally, you can effortlessly link and unlink user agents to various endpoints, ensuring your system's configurations are up-to-date and tailored to your specific needs."
      />
      {isLoading ? (
        <Loading />
      ) : (
        <Card elevation={0}>
          <CardContent>
            <DataTableGrid
              rows={
                data?.data.map((item) => {
                  return {
                    ...item,
                    id: item.user_agent_id,
                  };
                }) || []
              }
              columns={columns}
              handleSelectionChange={handleSelectionChange}
              rowSelectionModel={rowSelectionModel}
              itemsSelected={rowSelectionModel.length === 1}
              newLabel="Create User Agent"
              newOnClick={handleCreateUserAgent}
              columnVisibilityModel={columnVisible}
              getRowId={(row: UserAgent) => row?.user_agent_id}
              refreshData={handleDataRefetch}
              menuItems={[
                {
                  label: 'Link User Agent',
                  icon: <LinkIcon color="info" fontSize="small" />,
                  onClick: handleLinkUserAgent,
                },
                {
                  label: 'Unlink User Agent',
                  icon: <LinkOffIcon color="info" fontSize="small" />,
                  onClick: handleUnlinkUserAgent,
                },
                {
                  label: 'Delete',
                  icon: <DeleteForeverOutlinedIcon color="error" fontSize="small" />,
                  onClick: handleDelete,
                  hasDivider: true,
                },
              ]}
            />
          </CardContent>
        </Card>
      )}
      {userAgentCreate && (
        <DialogStandard
          open={userAgentCreate}
          handleClose={() => showUserAgentCreate(false)}
          title="Create User Agent"
        >
          <UserAgentCreate
            onSubmit={() => {
              showUserAgentCreate(false);
              refetch();
            }}
          />
        </DialogStandard>
      )}
      {userAgentLink && selectedItemId && (
        <DialogStandard
          open={userAgentLink}
          handleClose={() => showUserAgentLink(false)}
          title="Link User Agent"
        >
          <UserAgentLink
            onSubmit={() => {
              showUserAgentLink(false);
              refetch();
            }}
            userAgentId={selectedItemId}
          />
        </DialogStandard>
      )}
      {userAgentUnlink && selectedItemId && (
        <DialogStandard
          open={userAgentUnlink}
          handleClose={() => showUserAgentUnlink(false)}
          title="Unlink User Agent"
        >
          <UserAgentUnlink
            onSubmit={() => {
              showUserAgentUnlink(false);
              refetch();
            }}
            userAgentId={selectedItemId}
          />
        </DialogStandard>
      )}
      {showConfirmDelete && (
        <DialogConfirm
          open={showConfirmDelete}
          handleClose={() => setShowConfirmDelete(false)}
          handleConfirm={handleUserAgentDelete}
          title="Delete User Agent?"
          description={`Are you certain you wish to proceed with deleting ${selectedItemId}? Please note that this action is irreversible and will result in the permanent removal of the user agent.`}
          confirmationPhrase={selectedItemId}
        />
      )}
    </PageContent>
  );
};
